import { EndpointConfiguration, ReportColumn } from "@/types/evaluate";
import { useState, useRef, useMemo, useLayoutEffect } from "react";
import { useBandaid } from "../../utils/BandaidContext";
import { ModalStep } from "../ModalRouter";
import SourceName from "../SourceName";
import VariableList from "@/components/InputVariableConfigurator/VariableList";
import { Variable } from "@/components/InputVariableConfigurator/types";
import useAutoScroll from "@/components/InputVariableConfigurator/hooks/useAutoScroll";
import { Button } from "@/components/ui/button";
import { PlusIcon } from "@heroicons/react/solid";

const EndpointColumnBuilder = ({
  columnData,
  availableColumns,
  navigatePrevious,
  saveColumnAction,
  editable,
}: {
  columnData: Partial<ReportColumn>;
  availableColumns: ReportColumn[];
  navigatePrevious: () => void;
  saveColumnAction: (newColumnDataArg: ReportColumn) => void;
  editable: boolean;
}) => {
  const [url, setUrl] = useState<string | null>(
    columnData.configuration?.url || null,
  );
  const [isUrlValid, setIsUrlValid] = useState<boolean>(true);
  const [name, setName] = useState<string | null>(columnData.name || null);
  const [isNameValid, setIsNameValid] = useState<boolean>(true);
  const [headers, setHeaders] = useState<[string, string][]>(
    columnData.configuration?.headers
      ? Object.entries(columnData.configuration.headers)
      : [],
  );
  const ref = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);

  const variables = useMemo(
    () =>
      headers.map((header, index) => ({
        key: header[0],
        value: header[1],
        id: `header-${index}-${header[0]}`,
      })),
    [headers],
  );

  const { lastItemRef } = useAutoScroll(variables, ref);

  useLayoutEffect(() => {
    const scrollContainer = ref.current;
    if (scrollContainer) {
      scrollContainer.scrollTop = scrollPositionRef.current;
    }
  });

  const handleScroll = () => {
    if (ref.current) {
      scrollPositionRef.current = ref.current.scrollTop;
    }
  };

  const updateHeaders = (
    func: (inp: [string, string][]) => [string, string][],
  ) => {
    setHeaders(func);
  };

  const saveEndpointAction = () => {
    if (!name) {
      setIsNameValid(false);
      setIsUrlValid(true);
      return;
    } else if (!url) {
      setIsNameValid(true);
      setIsUrlValid(false);
      return;
    }

    setIsNameValid(true);
    setIsUrlValid(true);

    saveColumnAction({
      ...columnData,
      name: name,
      configuration: {
        url: url,
        headers: Object.fromEntries(headers),
      } as EndpointConfiguration,
    } as ReportColumn);
  };

  const bandaid = useBandaid();
  const examplePayload = {
    data: availableColumns.reduce((acc: any, column: ReportColumn) => {
      acc[column.name] = "_value_";
      return acc;
    }, {}),
  };

  return (
    <ModalStep
      navigatePrevious={navigatePrevious}
      navigateNext={saveEndpointAction}
      nextButtonText={editable ? "Save Step" : "Done"}
    >
      <div className="grid grid-cols-[auto,1fr] gap-4">
        <div className="col-span-2">
          <div className="text-left text-lg font-semibold">
            Configure API Endpoint
          </div>
          <div className="max-w-md text-left text-sm text-gray-500">
            Using the URL provided below, PromptLayer will send a POST request
            to your endpoint with a payload of all {bandaid ? "node" : "column"}{" "}
            data.
          </div>
        </div>
        <SourceName
          name={name!}
          columnCount={availableColumns.length}
          isValid={isNameValid}
          setName={setName}
          isNode={bandaid}
          readonly={!editable}
          variant="PROMPT_TEMPLATE"
        />
        <label htmlFor="url" className="self-center text-left">
          Endpoint URL:
        </label>
        <div>
          <input
            id="url"
            className={`w-full rounded border ${
              !isUrlValid ? "border-red-500" : "border-gray-300"
            } px-2 py-1 disabled:cursor-not-allowed disabled:bg-gray-50`}
            value={url || ""}
            placeholder="https://api.example.com/promptlayer"
            onChange={(e) => setUrl(e.target.value)}
            disabled={!editable}
          />
          {!isUrlValid && (
            <div className="mt-1 text-left text-xs text-red-500">
              URL is required
            </div>
          )}
        </div>
        <label className="col-span-2 font-medium">Headers</label>
        <div className="col-span-2 flex flex-col gap-y-2">
          <div
            ref={ref}
            className="max-h-[170px] overflow-y-auto"
            onScroll={handleScroll}
          >
            <VariableList
              variables={variables}
              placeholder="<API_KEY>"
              keyPlaceholder="X-API-Key"
              tooltip="Header value"
              keyTooltip="Header name"
              removeTooltip="Remove header"
              readOnly={!editable}
              updateInputVariable={(variable: Variable, index: number) => {
                const keyValue = String(variable.key);
                const stringValue =
                  typeof variable.value === "object"
                    ? JSON.stringify(variable.value)
                    : String(variable.value);

                updateHeaders((prev) => {
                  const newHeaders = [...prev];
                  newHeaders[index] = [keyValue, stringValue];
                  return newHeaders;
                });
              }}
              removeInputVariable={(index: number) => {
                updateHeaders((prev) => {
                  return prev.filter((_, i) => i !== index);
                });
              }}
              lastItemRef={lastItemRef}
            />
          </div>
          <Button
            variant="outline"
            className="w-full"
            onClick={() =>
              updateHeaders((prev) => {
                const newHeader: [string, string] = ["", ""];
                return [...prev, newHeader];
              })
            }
            disabled={!editable}
          >
            <PlusIcon className="mr-2 h-5 w-5 text-gray-600" />
            Add Header
          </Button>
        </div>
        <div className="col-span-2 m-2 flex flex-col gap-y-2 rounded-md bg-gray-200 p-3">
          <div className="text-left text-sm font-semibold">
            Example Payload (POST)
          </div>
          <pre className="max-w-md text-left font-mono text-xs text-gray-600">
            {JSON.stringify(examplePayload, null, 2).replace(/"/g, "")}
          </pre>
        </div>
      </div>
    </ModalStep>
  );
};

export default EndpointColumnBuilder;
